import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { firstValueFrom, take } from 'rxjs';

import { DashboardApiService } from 'src/app/api/services/dashboard-api.service';

import { Dashboard, DashboardPage, Period } from '../models/core.models';
import { UpdateDashboardPageParams } from '../models/widget.models';
import { NomObjectClass } from '../constants';

import { CurrentViewWidgetsService } from './current-view-widgets.service';
import {
  createDashboardPageSuccess,
  deleteDashboardPageSuccess,
  reorderDashboardPagesFail,
  reorderDashboardPagesSuccess,
  updateDashboardPagePeriod,
  updateDashboardPageSuccess,
} from '../state/object.actions';

import { AppState } from 'src/app/core/state/app.state';
import { selectCurrentObject, selectDashboardPages } from '../state';

/**
 * Dashboard-page CRUD.
 *
 * A service rather than mutation hooks because four components drive these writes
 * and the payload assembly, the last-page guard and the store broadcasts are
 * identical for all of them. It injects `DashboardApiService` directly, the
 * sanctioned service-to-service call.
 *
 * Every write broadcasts its `…Success` action: dashboard pages live on
 * `ObjectState.object.pages`, which stays NgRx because `selected_page_id` + `pages`
 * derive the widget list's query key.
 *
 * Create, delete and update are silent in both directions by design - no toasts.
 */
@Injectable({
  providedIn: 'root',
})
export class DashboardPagePersistenceService {
  private readonly store = inject(Store<AppState>);
  private readonly dashboardApi = inject(DashboardApiService);
  private readonly currentViewWidgets = inject(CurrentViewWidgetsService);

  createPage(dashboardId: string): Promise<DashboardPage | void> {
    return firstValueFrom(
      this.dashboardApi.createDashboardPage({ dashboardId }),
    ).then(
      (page) => {
        this.store.dispatch(createDashboardPageSuccess({ page }));
        return page;
      },
      () => undefined,
    );
  }

  /**
   * Delete one page. The last page cannot be deleted, and the page list is read
   * per call, so callers that delete several pages in a loop each work off their
   * own snapshot.
   *
   * The deleted page's widget-list cache entry is dropped directly. And
   * `deleteDashboardPageSuccess` is what picks the replacement page, whose
   * `selected_page_id` change loads the next page's widgets - so the dispatch
   * comes first, the cache drop second.
   */
  async deletePage(
    dashboardId: string,
    pageId: number,
  ): Promise<DashboardPage[] | void> {
    const originalPages = await firstValueFrom(
      this.store.select(selectDashboardPages).pipe(take(1)),
    );

    if (!originalPages || originalPages.length <= 1) {
      return;
    }

    return firstValueFrom(
      this.dashboardApi.deleteDashboardPage(dashboardId, pageId),
    ).then(
      (pages) => {
        this.store.dispatch(
          deleteDashboardPageSuccess({
            pages,
            deletedPageId: pageId,
            originalPages,
          }),
        );
        this.currentViewWidgets.removePage(pageId);
        return pages;
      },
      () => undefined,
    );
  }

  /**
   * The single page-update path - name, description, presentation visibility and
   * period all go through `POST /dashboard/:id/edit_widget_page`.
   */
  updatePage(params: UpdateDashboardPageParams): Promise<DashboardPage | void> {
    return firstValueFrom(this.dashboardApi.updateDashboardPage(params)).then(
      (page) => {
        this.store.dispatch(updateDashboardPageSuccess({ page }));
        return page;
      },
      () => undefined,
    );
  }

  /**
   * Period update for one page. `updateDashboardPagePeriod` is dispatched first as
   * the optimistic write that repaints the picker before the request lands.
   */
  async updatePagePeriod(
    pageId: number,
    period: Period,
  ): Promise<DashboardPage | void> {
    this.store.dispatch(updateDashboardPagePeriod({ pageId, period }));

    const currentObject = await firstValueFrom(
      this.store.select(selectCurrentObject).pipe(take(1)),
    );

    if (
      !currentObject ||
      currentObject.object_class_id !== NomObjectClass.DASHBOARD
    ) {
      return;
    }

    return this.updatePage({
      dashboard_id: currentObject.id,
      page_id: pageId,
      period,
    });
  }

  /**
   * Clear every page's period override so all pages follow the dashboard
   * period. Owns the store read and the per-page fan-out so consumers (the
   * header period picker) need no Dashboard knowledge.
   * TODO: one request per page; a mass page-update endpoint would collapse this.
   */
  async clearAllPagePeriods(): Promise<void> {
    const currentObject = await firstValueFrom(
      this.store.select(selectCurrentObject).pipe(take(1)),
    );

    if (
      !currentObject ||
      currentObject.object_class_id !== NomObjectClass.DASHBOARD
    ) {
      return;
    }

    const pages = (currentObject as Dashboard).pages ?? [];
    await Promise.all(
      pages.map((page) =>
        this.updatePage({
          dashboard_id: currentObject.id,
          page_id: page.id,
          period: null,
        }),
      ),
    );
  }

  /**
   * Reorder pages. The endpoint answers `{ success: boolean }` rather than the new
   * list, so success confirms the caller's optimistic order and a falsy `success`
   * (or a transport error) rolls back through `reorderDashboardPagesFail`.
   */
  reorderPages({
    dashboardId,
    orderedIds,
    orderedPages,
    rollbackPages,
  }: {
    dashboardId: string;
    orderedIds: number[];
    orderedPages: DashboardPage[];
    rollbackPages: DashboardPage[];
  }): Promise<void> {
    return firstValueFrom(
      this.dashboardApi.reorderDashboardPages({
        dashboardId,
        orderedPageIds: orderedIds,
      }),
    ).then(
      (response) => {
        if (response?.success) {
          this.store.dispatch(
            reorderDashboardPagesSuccess({ pages: orderedPages }),
          );
        } else {
          this.store.dispatch(
            reorderDashboardPagesFail({
              error: 'Server returned success: false',
              rollbackPages,
            }),
          );
        }
      },
      (error) => {
        this.store.dispatch(
          reorderDashboardPagesFail({
            error: error?.message || 'Failed to reorder pages',
            rollbackPages,
          }),
        );
      },
    );
  }
}
